<template>
  <div class="row mt-5">
    <div class="col-md-12">
      <v-chart v-if="radarOptions && dataReady"
               class="chart"
               :option="radarOptions"
               style="height:300px;"/>
    </div>
  </div>

  <div class="row mt-5">
    <h3>{{ $t('totalWeighted')  }}</h3>
    <div class="col-md-12">
      <table v-if="dataReady"
             class="table table-striped table-bordered table-responsive w-100">
        <thead>
        <tr>
          <th rowspan="3">
            {{ $t('dimension') }}
          </th>
        </tr>
        <tr>
          <th v-for="farm in farms"
              :colspan="Object.keys(results[farm.__id]).length">
            {{ farm.label }}
          </th>
        </tr>
        <tr>
          <template v-for="farm in farms">
            <th v-for="date in Object.keys(results[farm.__id])">
              {{ date }}
            </th>
          </template>
        </tr>

        </thead>
        <tbody>
        <tr v-for="dimension in selectedDimensions"
            :key="dimension.id">
          <td class="text-start">{{ dimension.name }}</td>
          <template v-for="(farm, index) in farms">
            <td v-for="date in Object.keys(results[farm.__id])">
              <span v-if="dataReady">
                {{
                  (this.totals[dimension.id][index] !== undefined) ? this.totals[dimension.id][index][farm.__id][date] : 0
                }} / 1
              </span>
            </td>
          </template>
        </tr>
        </tbody>
      </table>

      <p v-if="!dataReady">Loading Data...</p>

    </div>
  </div>
</template>

<script>
import {getDiagnosticsResults, getDimensionIndicators} from "@/utils";
import VChart from "@/main";

export default {
  components: {VChart},
  props: {
    farms: {
      type: Array,
      required: true,
    },
    selectedDimensions: {
      type: Array,
      required: true,
    },
    startDate: {
      type: String,
      required: true
    },
    endDate: {
      type: String,
      required: true
    },
    compareOverTime: {
      type: Boolean,
      required: false,
    },
    showAverage: {
      type: Boolean,
      required: false
    },
  },
  data() {
    return {
      totals: {},
      results: {},
      indicators: [],
      radarOptions: null,
      dataReady: false,
    }
  },
  async beforeMount() {
    await this.iterateDimensions();
  },
  methods: {
    async iterateDimensions() {
      for (const dimension of this.selectedDimensions) {
        this.totals[dimension.id] = [];
        let response = await getDimensionIndicators(dimension.id);
        if (response.status === 200) {
          this.indicators.push(response.data);
          await this.getResults(response.data, dimension);
        }
      }
      this.dataReady = true;
      if (this.selectedDimensions.length > 2) {
        this.drawRadarChart();
      } else {
        this.radarOptions = null;
      }
    },
    async getResults(indicators, dimension) {
      let farmIds = this.farms.map((farm) => farm.__id);
      let indicatorIds = indicators.map((indicator) => indicator.id);

      let response = await getDiagnosticsResults(farmIds, indicatorIds, this.showAverage, this.startDate, this.endDate);
      if (response.status === 200) {
        this.results = response.data;
        this.calculateTotals(indicators, dimension);
      }
    },
    calculateTotals(indicators, dimension) {
      this.farms.forEach(farm => {
        let total = [];
        Object.keys(this.results[farm.__id]).forEach(date => {
          total[date] = 0;
        });

        indicators.forEach(indicator => {
          if (this.results) {
            Object.keys(this.results[farm.__id]).forEach(date => {
                  total[date] += indicator.weight_in_dimension * (this.results[farm.__id][date]['values'][indicator.id]['indicator_value'] / indicator.maximum_value);
                }
            );
          } else {
            Object.keys(this.results[farm.__id]).forEach(date => {
              total[date] += 0;
            });
          }
        });

        Object.keys(this.results[farm.__id]).forEach(date => {
          total[date] = (total[date] / 100).toFixed(3);
        });

        let farmSum = {};
        farmSum[farm.__id] = total;
        this.totals[dimension.id].push(farmSum);
      })
    },
    drawRadarChart() {
      const chartDimensions = [];
      this.selectedDimensions.forEach(dimension => {
        chartDimensions.push({name: dimension.name, max: 1});
      });

      const chartData = [];
      const legendData = [];

      this.farms.forEach((farm, index) => {
        Object.keys(this.results[farm.__id]).forEach(date => {
          legendData.push(farm.label + " (" + date + ")");

          let dataSeries = {name: '', value: []};
          dataSeries.name = farm.label + " (" + date + ")";
          this.selectedDimensions.forEach(dimension => {
            if (this.totals[dimension.id][index] !== undefined) {
              dataSeries.value.push(this.totals[dimension.id][index][farm.__id][date]);
            }
          });

          chartData.push(dataSeries);
        });
      });

      this.radarOptions = {
        legend: {
          orient: 'vertical',
          top: 'top',
          left: 'left',
          data: legendData,
        },
        radar: {
          indicator: chartDimensions,
        },
        series: [
          {
            type: 'radar',
            data: chartData,
          },
        ],
      }
    },
  },
  watch: {
    async farms(newVal, oldVal) {
      this.dataReady = false;
      await this.iterateDimensions();
    },
    async selectedDimensions(newVal, oldVal) {
      this.dataReady = false;
      await this.iterateDimensions();
    }
  }
}
</script>